import { isApiError, unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import type { SourceView } from "../shared/types";
import { chunkNumber } from "./chunk-card";
import { isPdfPreview } from "./source-meta";
import { useChunks } from "./use-source-detail";

const NOT_FOUND = 404;
/** Signed preview URLs live a few minutes; refetch before they expire. */
const PREVIEW_STALE_MS = 4 * 60_000;

function usePreview(sourceId: string, page: number, enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.preview(wsId, sourceId, page),
    queryFn: () => unwrap(api.GET("/v1/sources/{source_id}/preview/{page}", { params: { path: { source_id: sourceId, page } } })),
    enabled,
    staleTime: PREVIEW_STALE_MS,
    retry: (count, error) => !(isApiError(error) && error.status === NOT_FOUND) && count < 2,
  });
}

/** Chunks (of the loaded pages) that cover this page — the API returns no bounding boxes. */
function useChunksOnPage(sourceId: string, page: number): number[] {
  const chunks = useChunks(sourceId, "");
  return (chunks.data?.pages.flat() ?? [])
    .filter((c) => c.page_start !== null && c.page_start <= page && (c.page_end ?? c.page_start) >= page)
    .map(chunkNumber);
}

/** Page preview via `GET /v1/sources/{id}/preview/{page}` (signed URL: PDF page or page image). */
export function PreviewPanel({ source }: { source: SourceView }) {
  const { t } = useTranslation();
  const k = "deployKnowledge.sourceDetail.preview";
  const total = source.page_count ?? 0;
  const [page, setPage] = useState(1);
  const preview = usePreview(source.id, page, total > 0);
  const onPage = useChunksOnPage(source.id, page);
  if (total === 0) return <EmptyState icon="file" headingLevel={3} title={t(`${k}.noPagesTitle`)} description={t(`${k}.noPagesBody`)} />;
  const missing = isApiError(preview.error) && preview.error.status === NOT_FOUND;
  return (
    <div className="bg-sunken p-5">
      <div className="mb-3 flex items-center justify-between gap-3 text-xs text-fg-muted">
        <span aria-live="polite">
          {t(`${k}.pageOf`, { page, total })} · {onPage.length > 0 ? t(`${k}.highlighted`, { chunks: onPage.map((n) => `#${String(n)}`).join(", ") }) : t(`${k}.noChunksOnPage`)}
        </span>
        <div className="flex gap-1">
          <Button variant="secondary" size="sm" iconOnly aria-label={t(`${k}.previous`)} disabled={page <= 1} onClick={() => { setPage((p) => p - 1); }}>
            <Icon name="chevron-left" />
          </Button>
          <Button variant="secondary" size="sm" iconOnly aria-label={t(`${k}.next`)} disabled={page >= total} onClick={() => { setPage((p) => p + 1); }}>
            <Icon name="chevron-right" />
          </Button>
        </div>
      </div>
      <div className="mx-auto max-w-2xl overflow-hidden rounded-xs border border-line bg-surface shadow-card">
        {preview.isPending ? (
          <Skeleton className="h-[640px] rounded-none" />
        ) : missing ? (
          <EmptyState icon="file" headingLevel={3} title={t(`${k}.unavailableTitle`)} description={t(`${k}.unavailableBody`)} />
        ) : preview.isError ? (
          <ErrorState headingLevel={3} title={t("deployKnowledge.loadError")} onRetry={() => void preview.refetch()} retryLabel={t("common.retry")} />
        ) : isPdfPreview(preview.data.url) ? (
          <iframe key={preview.data.url} src={preview.data.url} title={t(`${k}.frameTitle`, { page, name: source.title })} className="h-[720px] w-full border-0" />
        ) : (
          <img src={preview.data.url} alt={t(`${k}.imageAlt`, { page, name: source.title })} className="h-auto w-full" />
        )}
      </div>
    </div>
  );
}
